import React, { useState } from 'react';
import { CreditCard, Bitcoin, Wallet, Globe, Check } from 'lucide-react';

const PaymentMethodSelector = () => {
  const [selectedMethod, setSelectedMethod] = useState('mastercard');
  const [formData, setFormData] = useState({
    nameOnCard: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    email: ''
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const paymentMethods = [
    { id: 'mastercard', name: 'Mastercard', icon: '💳', color: 'bg-red-500' },
    { id: 'visa', name: 'Visa', icon: '💳', color: 'bg-blue-600' },
    { id: 'paypal', name: 'PayPal', icon: '🅿️', color: 'bg-blue-500' },
    { id: 'bitcoin', name: 'Bitcoin', icon: '₿', color: 'bg-orange-500' },
    { id: 'hellobux', name: 'Hello Bux', icon: '💰', color: 'bg-green-500' },
    { id: 'paystack', name: 'Paystack', icon: '📱', color: 'bg-teal-500' },
    { id: 'flutterwave', name: 'Flutterwave', icon: '🌊', color: 'bg-purple-500' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckout = () => {
    if (selectedMethod === 'mastercard' || selectedMethod === 'visa') {
      if (!formData.nameOnCard || !formData.cardNumber || !formData.expiryDate || !formData.cvv || !formData.email || !agreedToTerms) {
        alert('Please fill in all required fields and agree to terms');
        return;
      }
    }
    alert(`Processing payment with ${paymentMethods.find(m => m.id === selectedMethod)?.name}`);
  };

  const renderCardForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Name on Card
        </label>
        <input
          type="text"
          name="nameOnCard"
          value={formData.nameOnCard}
          onChange={handleInputChange}
          placeholder="Enter your name"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Card Number
          </label>
          <input
            type="text"
            name="cardNumber"
            value={formData.cardNumber}
            onChange={handleInputChange}
            placeholder="1234 5678 9012 3456"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            MM/YYYY
          </label>
          <input
            type="text"
            name="expiryDate"
            value={formData.expiryDate}
            onChange={handleInputChange}
            placeholder="12/2025"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CVV/CVC
          </label>
          <input
            type="text"
            name="cvv"
            value={formData.cvv}
            onChange={handleInputChange}
            placeholder="123"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email address
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="Enter your email"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>
  );

  const renderAlternativePayment = () => {
    const method = paymentMethods.find(m => m.id === selectedMethod);
    return (
      <div className="text-center py-8">
        <div className={`w-16 h-16 ${method?.color} rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4`}>
          {method?.icon}
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          Pay with {method?.name}
        </h3>
        <p className="text-gray-600 mb-6">
          You have selected {method?.name} as your payment method.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300">
          <p className="text-sm text-gray-500">
            Click checkout to continue with {method?.name} payment
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Hello<span className="text-blue-200">cer</span></h1>
              <p className="text-blue-100 text-sm mt-1">Secure Payment</p>
            </div>
            <CreditCard className="w-8 h-8 text-blue-200" />
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            Choose payment method
          </h2>

          {/* Payment Method Grid */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            {paymentMethods.map((method) => (
              <button
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`relative p-3 rounded-lg border-2 transition-all duration-200 hover:scale-105 ${
                  selectedMethod === method.id
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-8 h-8 ${method.color} rounded flex items-center justify-center text-white text-sm font-bold mx-auto`}>
                  {method.icon}
                </div>
                <p className="text-xs font-medium text-gray-700 mt-1 truncate">
                  {method.name}
                </p>
                {selectedMethod === method.id && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Payment Form or Alternative Display */}
          <div className="mb-6">
            {selectedMethod === 'mastercard' || selectedMethod === 'visa' 
              ? renderCardForm() 
              : renderAlternativePayment()
            }
          </div>

          {/* Terms Checkbox */}
          <div className="flex items-start space-x-2 mb-6">
            <input
              type="checkbox"
              id="terms"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="terms" className="text-sm text-gray-600 leading-5">
              By continuing the order, I accept the terms of the user agreement
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleCheckout}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              CHECKOUT
            </button>
            <button
              onClick={() => {
                setFormData({
                  nameOnCard: '',
                  cardNumber: '',
                  expiryDate: '',
                  cvv: '',
                  email: ''
                });
                setAgreedToTerms(false);
              }}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodSelector;